import React, { useState, useEffect, useRef } from 'react';
import { Lock, AlertCircle, Mail, Phone } from '../common/IconsComponent';

const VerificationInput = ({
  name,
  verificationCode,
  onVerificationCodeChange,
  onVerify,
  attempts,
  maxAttempts,
  containerRef,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const attemptsRemaining = maxAttempts - attempts;
  const isEmail = name === 'email';

  useEffect(() => {
    if (containerRef?.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 250);

    return () => clearTimeout(timer);
  }, [containerRef]);

  return (
    <div
      ref={containerRef}
      className="my-4 overflow-hidden rounded-xl shadow-md"
      dir="rtl"
    >
      {/* Header */}
      <div className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-indigo-800 p-3 text-white">
        <div className="rounded-full bg-white/20 p-2">
          {isEmail
            ? <Mail size={15} className="text-white" />
            : <Phone size={15} className="text-white" />
          }
        </div>
        <p className="text-sm font-medium">
          קוד אימות נשלח ל{isEmail ? 'כתובת האימייל' : 'טלפון הנייד'} שלך
        </p>
      </div>

      {/* Body */}
      <div className="border border-indigo-100 bg-white p-4 sm:p-5">
        <p className="mb-2 text-sm text-gray-500">הזן את קוד האימות בן 4 הספרות:</p>

        <div
          className={`relative mb-4 flex flex-row-reverse justify-center gap-2 sm:gap-3 cursor-text rounded-xl p-3 transition-all ${
            isFocused
              ? 'bg-indigo-50 ring-2 ring-indigo-300'
              : 'bg-gray-50 hover:bg-gray-100'
          }`}
          onClick={() => inputRef.current?.focus()}
        >
          {[0, 1, 2, 3].map((index) => (
            <div
              key={index}
              className={`flex h-12 w-11 items-center justify-center rounded-lg border-2 transition-all sm:h-14 sm:w-12 ${
                verificationCode.length > index
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : index === verificationCode.length
                  ? 'border-indigo-400 border-dashed bg-white'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <span className="text-xl font-bold sm:text-2xl">
                {verificationCode[index] || ''}
              </span>
            </div>
          ))}

          <input
            ref={inputRef}
            type="text"
            value={verificationCode}
            onChange={onVerificationCodeChange}
            maxLength={4}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            dir="ltr"
            className="absolute opacity-0 pointer-events-none"
          />
        </div>

        {attempts > 0 && (
          <div className={`mb-4 flex items-start gap-1.5 rounded-lg border p-2.5 ${
            attemptsRemaining === 1
              ? 'border-red-200 bg-red-50'
              : 'border-amber-200 bg-amber-50'
          }`}>
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <p className={`text-xs leading-relaxed ${
              attemptsRemaining === 1 ? 'text-red-700' : 'text-amber-700'
            }`}>
              {attemptsRemaining === 1
                ? 'זהו ניסיון האימות האחרון שלך!'
                : `ניסיון האימות נכשל. נשארו לך עוד ${attemptsRemaining} ניסיונות.`}
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={onVerify}
          disabled={verificationCode.length !== 4}
          className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 font-medium transition-all ${
            verificationCode.length === 4
              ? 'bg-gradient-to-r from-indigo-500 to-indigo-700 text-white shadow-md hover:shadow-lg hover:opacity-90'
              : 'cursor-not-allowed bg-gray-100 text-gray-400'
          }`}
        >
          <Lock size={15} />
          <span>אמת קוד</span>
        </button>
      </div>
    </div>
  );
};

export default VerificationInput;